import { Column, defaultWidth } from "@/components/CellGrid/Column";
import { defaultLabelColumnWidth } from "@/components/CellGrid/LabelColumn";
import { Row } from "@/components/CellGrid/Row";
import { lineSize } from "@/components/CellGrid/Waterfall";
import { defaultHeight } from "@/components/CellGrid/cell/Wrapper";
import { Epoch } from "@/financeModels/owasco/types";
import {
  periodFormatIdAtom,
  workingCapitalEpochsAtomsContainer,
  workingCapitalFormulaAtom,
} from "@/financeModels/owasco/workingCapital";
import { formulaRangeAtom } from "@/financeModels/owasco/workingCapitalEpoch";
import { useIsModelReadOnly } from "@/hooks/useFinancialDeckPermissions";
import { ZIndexOrder } from "@/theme";
import {
  ActionIcon,
  BoxComponentProps,
  Button,
  Center,
  Group,
  Menu,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { WritableAtom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { memo, useEffect, useState } from "react";
import { BiSolidMessageSquareAdd } from "react-icons/bi";
import { PiPencil } from "react-icons/pi";
import { TbTrash } from "react-icons/tb";
import { css } from "vite-plugin-inline-css-modules";

const { createEpochAtom, deleteEpochAtom, orderedEpochsMolecule } =
  workingCapitalEpochsAtomsContainer;

export const DateEpochRow = ({ ...props }: BoxComponentProps) => {
  const epochAtoms = useAtomValue(orderedEpochsMolecule);
  const periodFormatId = useAtomValue(periodFormatIdAtom);

  const createEpoch = useSetAtom(createEpochAtom);
  const [isModelReadOnly] = useIsModelReadOnly();

  const workingCapitalFormula = useAtomValue(workingCapitalFormulaAtom);
  const formulaRange = useAtomValue(formulaRangeAtom);

  const label = formulaRange
    .map((dateTimestamp) => new Date(dateTimestamp).toLocaleDateString("en-US"))
    .join(" - ");

  return (
    <Row
      {...props}
      style={{
        position: "sticky",
        top: 0,
        zIndex: ZIndexOrder.CellGridEpochRow,
        borderBottom: "1px solid #ddd",
      }}
      pl={defaultLabelColumnWidth + lineSize}
      bg={"gray.3"}
    >
      <Column w={defaultWidth}>
        <Center
          bg={"gray.2"}
          h={36}
          style={{
            borderBottom: "2px solid #ddd",
            borderRight: "1px solid #ddd",
            borderRadius: "4px 4px 0 0",
          }}
        >
          <Stack gap={0}>
            <Text size={"sm"} fw={"bold"} ta={"center"} lh={1.2}>
              {workingCapitalFormula.label}
            </Text>
            <Text size={"xs"} fw={500} ta={"center"} lh={1}>
              {label}
            </Text>
          </Stack>
        </Center>
      </Column>

      {epochAtoms.map((atom) => (
        <Column key={`${atom}`}>
          <Center
            bg={"white"}
            h={36}
            pl={10}
            style={{
              borderBottom: "2px solid #ddd",
              borderRight: "1px solid #ddd",
              borderRadius: "4px 4px 0 0",
            }}
          >
            <CellContent atom={atom} />
          </Center>
        </Column>
      ))}

      {!isModelReadOnly && (
        <Button
          leftSection={<BiSolidMessageSquareAdd size={22} />}
          h={defaultHeight}
          w={defaultWidth}
          onClick={() => createEpoch(undefined, periodFormatId)}
          justify="left"
          mx="sm"
          //variant="default"
        >
          Add Period
        </Button>
      )}
    </Row>
  );
};

// For some reason my linter isnt happy moving this into the Cell props?
// So its here....
type CellProps = {
  atom: WritableAtom<Epoch, [Epoch], void>;
};

export const CellContent = memo(
  ({ atom, ...props }: BoxComponentProps & CellProps) => {
    const [isModelShared] = useIsModelReadOnly();
    const [epoch, setEpoch] = useAtom(atom);

    //use year-month-day

    const label = new Date(epoch.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <EditMenu epoch={epoch} onChange={setEpoch}>
        <Group
          className={classes.showIconOnHover}
          gap={0}
          justify="center"
          align="flex-start"
          {...props}
        >
          <Text fz={"sm"} fw={500}>
            {label}
          </Text>
          {!isModelShared && <PiPencil color="#999" />}
        </Group>
      </EditMenu>
    );
  },
);

const EditMenu = ({
  epoch,
  onChange,
  children,
}: {
  epoch: Epoch;
  onChange: (epoch: Epoch) => void;
  children: React.ReactNode;
}) => {
  const [isModelReadOnly] = useIsModelReadOnly();
  const [opened, setOpened] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [dateInputValue, setDateInputValue] = useState(new Date(epoch.date));
  const deleteEpoch = useSetAtom(deleteEpochAtom);

  useEffect(() => {
    setDateInputValue(new Date(epoch.date));
  }, [epoch.date]);

  const onSubmit = (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (!dateInputValue) return;
    onChange({ ...epoch, date: dateInputValue.getTime() });
    setOpened(false);
  };

  const onMenuChange = () => {
    if (isModelReadOnly) return;
    setOpened(!opened);
    setShowConfirmDelete(false);

    if (opened) {
      onSubmit();
    } else {
      setDateInputValue(new Date(epoch.date));
    }
  };

  const onEpochDelete = () => {
    setOpened(!opened);
    deleteEpoch(epoch.id);
  };

  return (
    <Menu
      width={defaultWidth}
      shadow="md"
      withArrow
      position="bottom"
      opened={opened}
      onChange={onMenuChange}
    >
      <Menu.Target>{children}</Menu.Target>

      <Menu.Dropdown>
        {!showConfirmDelete && (
          <Stack p="xs">
            <Group justify="space-between">
              <Text>Edit Period</Text>
              <ActionIcon
                color="red.8"
                size="sm"
                onClick={() => {
                  setShowConfirmDelete(true);
                }}
              >
                <TbTrash />
              </ActionIcon>
            </Group>
            <form onSubmit={onSubmit}>
              <Stack>
                <TextInput
                  type="date"
                  value={dateInputValue.toISOString().split("T")[0]}
                  onChange={(e) => {
                    e.preventDefault();
                    const date = e.currentTarget.valueAsDate || new Date();
                    const utcDate = new Date(
                      date.getTime() + date.getTimezoneOffset() * 60000,
                    );
                    setDateInputValue(utcDate);
                  }}
                />
                <Button type="submit">Update</Button>
              </Stack>
            </form>
          </Stack>
        )}
        {showConfirmDelete && (
          <Stack p="xs">
            <Text ta="center">Are you sure? This can't be undone.</Text>
            <Button onClick={onEpochDelete} color="red">
              Delete
            </Button>
          </Stack>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

const classes = css`
  .showIconOnHover {
    cursor: pointer;

    svg {
      opacity: 0;
    }

    &:hover {
      svg {
        opacity: 1;
      }
    }
  }
`;
